/**
 * PaymentDetailModal — детали транзакции: реквизиты (сумма/дата/способ/ID) и
 * действия (открыть клиента, отметить возврат — только для успешных).
 * Десктоп — центр-модалка, мобилка — bottom-sheet; контент общий.
 */

import type {ReactNode} from 'react';
import {RotateCcw, UserRound, X} from 'lucide-react';

import type {PaymentRow} from '@services/payments';
import {SheetGrabber} from '@components/SheetGrabber';
import {Dialog, DialogContent} from '@components/ui/Dialog';
import {Sheet, SheetContent} from '@components/ui/Sheet';
import {ClientTypeIcon} from '@pages/Clients/ClientTypeIcon';

import {amountClass, fmtRowDate, fmtSignedSom} from '../Payments.utils';
import {PaymentStatusPill} from '../PaymentStatusPill';

type PaymentDetailModalProps = {
  payment: PaymentRow | null;
  mobile?: boolean;
  onClose: () => void;
  onOpenClient: (row: PaymentRow) => void;
  onRefund: (row: PaymentRow) => void;
};

export const PaymentDetailModal = ({
  payment,
  mobile = false,
  onClose,
  onOpenClient,
  onRefund,
}: PaymentDetailModalProps) => {
  const open = payment !== null;
  const body = payment && (
    <>
      {mobile ? (
        <SheetGrabber className="mb-4" />
      ) : (
        <button
          onClick={onClose}
          aria-label="Закрыть"
          className="absolute right-4 top-4 grid size-[30px] place-items-center rounded-lg bg-secondary text-muted-foreground"
        >
          <X className="size-4" strokeWidth={2} />
        </button>
      )}

      <div className="flex items-center gap-3">
        <ClientTypeIcon type={payment.clientType} size={46} />
        <div className="flex-1">
          <div className="text-[17px] font-bold">{payment.client}</div>
          <div className="mt-px text-[12.5px] text-muted-foreground">
            {payment.type} · {payment.period}
          </div>
        </div>
        <PaymentStatusPill status={payment.status} />
      </div>

      <div className="mt-4 flex flex-col gap-[11px] rounded-[13px] border border-skr-line2 bg-skr-head px-4 py-3.5">
        <Row label="Сумма">
          <span className={`font-mono text-base font-semibold ${amountClass(payment)}`}>
            {fmtSignedSom(payment.amount)} сом
          </span>
        </Row>
        <Row label="Дата">
          <span className="font-mono text-[13.5px]">{fmtRowDate(payment.date)}</span>
        </Row>
        <Row label="Способ">
          <span className="text-[13.5px]">{payment.method}</span>
        </Row>
        <Row label="ID транзакции">
          <span className="font-mono text-[12.5px] text-muted-foreground">{payment.txnId}</span>
        </Row>
      </div>

      <div className="mt-4 flex flex-col gap-2.5">
        <button
          onClick={() => onOpenClient(payment)}
          className="flex h-12 items-center justify-center gap-2 rounded-xl border border-input bg-card text-[14.5px] font-semibold text-skr-ink hover:bg-secondary"
        >
          <UserRound className="size-[18px]" strokeWidth={1.8} />
          Открыть клиента
        </button>
        {payment.status === 'success' && (
          <button
            onClick={() => onRefund(payment)}
            className="flex h-12 items-center justify-center gap-2 rounded-xl border border-[#F1E4C9] bg-[#FBF6EC] text-[14.5px] font-semibold text-skr-warn"
          >
            <RotateCcw className="size-[18px]" strokeWidth={1.8} />
            Отметить возврат
          </button>
        )}
      </div>
    </>
  );

  if (mobile) {
    return (
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent side="bottom" showClose={false} className="gap-0 rounded-t-[22px] p-5 pb-6">
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        showClose={false}
        className="w-[440px] max-w-[calc(100vw-2rem)] gap-0 rounded-[18px] p-6"
      >
        {body}
      </DialogContent>
    </Dialog>
  );
};

const Row = ({label, children}: {label: string; children: ReactNode}) => (
  <div className="flex items-center justify-between">
    <span className="text-[12.5px] text-muted-foreground">{label}</span>
    {children}
  </div>
);
